import React, { useEffect, useState, useCallback } from "react";
import {
  Form,
  Select,
  DatePicker,
  TimePicker,
  InputNumber,
  Radio,
  Button,
  Spin,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import api from "api";
import tipOfferApi from "api/tipOfferApi";
import { GOLFER_ROUTES } from "constants/routesPath";
import { BANGKOK_TZ, SELECTION_CUTOFF_MINUTES } from "utils/tipOffer";
import { TIP_OFFER_PLAY_MODE_OPTIONS } from "utils/constants";
import { mapCreateOfferError, CREATE_OFFER_MESSAGES } from "./errorMap";
import ComingSoon from "components/coming-soon";

// Minimum tap-target size for accessibility (spec: >= 44px).
const TAP_TARGET = 44;
// The backend rejects tee times closer than SELECTION_CUTOFF_MINUTES; keep a few
// extra minutes for filling in the form.
const MIN_LEAD_MINUTES = SELECTION_CUTOFF_MINUTES + 5;

/**
 * Earliest tee time (Bangkok) that can still be offered on `date` (YYYY-MM-DD),
 * as minutes since midnight: 0 for a future date, Infinity if none is left today.
 */
export function earliestTeeMinute(date, now = dayjs().tz(BANGKOK_TZ)) {
  const today = now.format("YYYY-MM-DD");
  if (!date || date > today) return 0;
  if (date < today) return Infinity;
  const earliest = now.add(MIN_LEAD_MINUTES, "minute");
  if (earliest.format("YYYY-MM-DD") !== today) return Infinity;
  return earliest.hour() * 60 + earliest.minute();
}

const TOO_SOON_MESSAGE =
  `เวลาทีออฟต้องห่างจากตอนนี้อย่างน้อย ${MIN_LEAD_MINUTES} นาที ` +
  `(Tee time must be at least ${MIN_LEAD_MINUTES} minutes from now)`;

/**
 * "Book me a caddie" — golfer offer-creation screen.
 *
 * Golfer-facing name is "Book me a caddie" (จองแคดดี้ให้ฉัน); the folder, route,
 * and API identifiers stay `tip-offer` to match the backend contract.
 *
 * The golfer picks a golf course (reusing the same master-data course source as
 * search-caddy), a tee date/time, and a tip amount, then submits via
 * tipOfferApi.createOffer. On success we show how many caddies were notified and
 * route to the offer detail. Backend validation errors (missing field, past tee
 * time, tip out of bounds) are surfaced as bilingual messages.
 *
 * Requirements: 9.1, 9.2, 9.9, 9.10
 */
export default function TipOfferCreate({ is_comming }) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [golfCourseList, setGolfCourseList] = useState([]);
  const [isLoadingGolfCourses, setIsLoadingGolfCourses] = useState(false);
  const [maxTip, setMaxTip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tfDate, setTfDate] = useState(null);

  // Load the golf courses (same master-data source used by search-caddy) and
  // the controller config for the tip upper-bound hint (controller.max_caddy_tips).
  useEffect(() => {
    let cancelled = false;

    async function loadGolfCourses() {
      setIsLoadingGolfCourses(true);
      try {
        const { data, status } =
          await api.masterDataApi.getMasterDataGolfCourses();
        if (cancelled || status !== 200) return;
        setGolfCourseList(data?.data || []);
      } catch (error) {
        console.error("Golf course fetch error:", error);
      } finally {
        if (!cancelled) setIsLoadingGolfCourses(false);
      }
    }

    async function loadTipBound() {
      try {
        const { data, status } =
          await api.masterDataApi.getMasterDataController();
        if (cancelled || status !== 200) return;
        const value = data?.data?.max_caddy_tips;
        if (value !== null && value !== undefined && value !== "") {
          setMaxTip(Number(value));
        }
      } catch (error) {
        // Non-fatal: the backend still validates the bound on submit.
        console.error("Controller config fetch error:", error);
      }
    }

    loadGolfCourses();
    loadTipBound();

    return () => {
      cancelled = true;
    };
  }, []);

  // "Today" is the Bangkok date, whatever timezone the phone is in.
  const disablePastDates = (current) =>
    current &&
    current.format("YYYY-MM-DD") < dayjs().tz(BANGKOK_TZ).format("YYYY-MM-DD");

  const disabledTeeTime = () => {
    const earliest = earliestTeeMinute(tfDate);
    return {
      disabledHours: () =>
        Array.from({ length: 24 }, (_, h) => h).filter(
          (h) => h * 60 + 59 < earliest
        ),
      disabledMinutes: (hour) =>
        Array.from({ length: 60 }, (_, m) => m).filter(
          (m) => hour * 60 + m < earliest
        ),
    };
  };

  const handleSubmit = useCallback(
    async (values) => {
      if (submitting) return;
      setSubmitting(true);

      // Combine the chosen tee date + time into a single moment; the backend
      // expects tf_date (YYYY-MM-DD) and tf_time (a full date-time).
      const teeDate = dayjs(values.teeOffDate);
      const teeTime = dayjs(values.teeOffTime);
      const teeDateTime = teeDate
        .hour(teeTime.hour())
        .minute(teeTime.minute())
        .second(0)
        .millisecond(0);

      const payload = {
        golf_course_id: values.golfCourse,
        tf_date: teeDate.format("YYYY-MM-DD"),
        tf_time: teeDateTime.format("YYYY-MM-DD HH:mm:ss"),
        tip_amount: values.tipAmount,
        play_mode: values.playMode,
      };

      try {
        const response = await tipOfferApi.createOffer(payload);
        const body = response?.data || {};
        const created = body.data || {};
        const notifiedCount = created.notified_count ?? 0;

        // The list page shows this message: one from this page's messageApi
        // would unmount with the page.
        navigate(GOLFER_ROUTES.TIP_OFFER_LIST, {
          state: {
            successMessage:
              `สร้างรายการสำเร็จ แจ้งเตือนแคดดี้แล้ว ${notifiedCount} คน ` +
              `(Offer created — ${notifiedCount} caddie${
                notifiedCount === 1 ? "" : "s"
              } notified)`,
          },
        });
      } catch (error) {
        messageApi.error(mapCreateOfferError(error));
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, messageApi, navigate]
  );

  const handleSubmitFailed = useCallback(() => {
    const { th, en } = CREATE_OFFER_MESSAGES.missingField;
    messageApi.error(`${th} (${en})`);
  }, [messageApi]);

  // Matches the existing feature-gate convention (see search-caddy): when the
  // gate flag is provided and falsy, show the coming-soon placeholder. When the
  // prop is omitted entirely the screen renders normally.
  if (is_comming !== undefined && !is_comming) {
    return <ComingSoon />;
  }

  return (
    <div style={{ padding: 16, maxWidth: 600, margin: "0 auto" }}>
      {contextHolder}
      <h2 style={{ marginBottom: 4 }}>จองแคดดี้ให้ฉัน</h2>
      <p style={{ marginTop: 0, color: "#666" }}>Book me a caddie</p>
      <Button
        type="link"
        style={{ padding: 0, height: TAP_TARGET }}
        onClick={() => navigate(GOLFER_ROUTES.TIP_OFFER_LIST)}
      >
        ดูข้อเสนอของฉัน (My offers)
      </Button>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={handleSubmitFailed}
        scrollToFirstError
      >
        <Form.Item
          name="golfCourse"
          label={<b>สนามกอล์ฟ (Golf course)</b>}
          rules={[
            {
              required: true,
              message: "กรุณาเลือกสนามกอล์ฟ (Please select a golf course)",
            },
          ]}
        >
          <Select
            allowClear
            showSearch
            aria-label="เลือกสนามกอล์ฟ (Select golf course)"
            placeholder="เลือกสนามกอล์ฟ (Select golf course)"
            loading={isLoadingGolfCourses}
            style={{ width: "100%", minHeight: TAP_TARGET }}
            filterOption={(input, option) =>
              option?.children?.toLowerCase()?.includes(input.toLowerCase())
            }
            notFoundContent={
              isLoadingGolfCourses ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <Spin
                    indicator={<LoadingOutlined spin />}
                    size="small"
                  />{" "}
                  <span style={{ color: "#999" }}>
                    กำลังโหลดสนามกอล์ฟ...
                  </span>
                </div>
              ) : (
                <span style={{ color: "#999" }}>
                  ไม่พบสนามกอล์ฟ (No golf courses)
                </span>
              )
            }
          >
            {golfCourseList?.map((golfCourse) => (
              <Select.Option key={golfCourse.id} value={golfCourse.id}>
                {golfCourse.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="teeOffDate"
          label={<b>วันที่ทีออฟ (Tee-off date)</b>}
          rules={[
            {
              required: true,
              message: "กรุณาเลือกวันที่ทีออฟ (Please select a tee-off date)",
            },
          ]}
        >
          <DatePicker
            allowClear
            inputReadOnly
            aria-label="เลือกวันที่ทีออฟ (Select tee-off date)"
            placeholder="เลือกวันที่ทีออฟ (Select date)"
            disabledDate={disablePastDates}
            format="DD-MMMM-YYYY"
            style={{ width: "100%", height: TAP_TARGET }}
            onChange={(date) => {
              setTfDate(date ? dayjs(date).format("YYYY-MM-DD") : null);
              if (!date) {
                form.setFieldValue("teeOffTime", null);
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="teeOffTime"
          label={<b>เวลาทีออฟ (Tee-off time)</b>}
          rules={[
            {
              required: true,
              message: "กรุณาเลือกเวลาทีออฟ (Please select a tee-off time)",
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const t = dayjs(value);
                return t.hour() * 60 + t.minute() < earliestTeeMinute(tfDate)
                  ? Promise.reject(new Error(TOO_SOON_MESSAGE))
                  : Promise.resolve();
              },
            },
          ]}
        >
          <TimePicker
            allowClear
            inputReadOnly
            aria-label="เลือกเวลาทีออฟ (Select tee-off time)"
            placeholder="เลือกเวลาทีออฟ (Select time)"
            disabled={!tfDate}
            format="HH:mm"
            minuteStep={5}
            disabledTime={disabledTeeTime}
            style={{ width: "100%", height: TAP_TARGET }}
          />
        </Form.Item>

        <Form.Item
          name="playMode"
          label={<b>รูปแบบการเล่น (Play mode)</b>}
          rules={[
            {
              required: true,
              message:
                "กรุณาเลือกรูปแบบการเล่น (Please choose cart or walking)",
            },
          ]}
        >
          <Radio.Group
            aria-label="เลือกรูปแบบการเล่น รถกอล์ฟ หรือ เดิน (Select cart or walking)"
            optionType="button"
            buttonStyle="solid"
            style={{ width: "100%" }}
          >
            {TIP_OFFER_PLAY_MODE_OPTIONS.map((mode) => (
              <Radio.Button
                key={mode.value}
                value={mode.value}
                style={{
                  width: "50%",
                  textAlign: "center",
                  height: TAP_TARGET,
                  lineHeight: `${TAP_TARGET}px`,
                }}
              >
                {mode.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="tipAmount"
          label={<b>จำนวนทิป (Tip amount, บาท)</b>}
          extra={
            maxTip !== null
              ? `สูงสุด ${maxTip} บาท (Maximum ${maxTip} THB)`
              : undefined
          }
          rules={[
            {
              required: true,
              message: "กรุณาระบุจำนวนทิป (Please enter a tip amount)",
            },
            {
              type: "number",
              min: 1,
              message:
                "จำนวนทิปต้องมากกว่าศูนย์ (Tip amount must be greater than zero)",
            },
            ...(maxTip !== null
              ? [
                  {
                    type: "number",
                    max: maxTip,
                    message: `จำนวนทิปต้องไม่เกิน ${maxTip} บาท (Tip amount must not exceed ${maxTip})`,
                  },
                ]
              : []),
          ]}
        >
          <InputNumber
            aria-label="จำนวนทิป (Tip amount in baht)"
            placeholder="เช่น 500 (e.g. 500)"
            min={1}
            precision={0}
            max={maxTip !== null ? maxTip : undefined}
            style={{ width: "100%", height: TAP_TARGET, lineHeight: `${TAP_TARGET}px` }}
          />
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting}
            disabled={submitting}
            style={{ height: TAP_TARGET }}
            aria-label="จองแคดดี้ให้ฉัน (Book me a caddie)"
          >
            {submitting
              ? "กำลังส่ง... (Submitting...)"
              : "จองแคดดี้ให้ฉัน (Book me a caddie)"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
